/**
 * Grid Combat Simulator — Dev tool for testing tile-based tactical combat.
 * Combines the grid-map engine (rendering, pathfinding, FOV) with the
 * grid combat engine (state machine, dice, AI).
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Swords, Play, RotateCcw,
  Shield as ShieldIcon, Crosshair, Footprints, Sparkles, Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { GridMapCanvas } from '@/components/game-ui/GridMapCanvas';
import type { TileHighlight, PathPreviewPoint, TargetIndicator } from '@/components/game-ui/GridMapCanvas';
import { ParticipantRow, CombatLogViewer } from '@/components/game-ui/combat/shared/CombatComponents';

import type {
  GridMapState, GridMap, RenderConfig, Entity, MoveDirection,
} from '@omen-quest/grid-map';
import {
  generateMap, createGridMapState,
  computeVisibility, DEFAULT_RENDER_CONFIG,
  tryMove, findPath, coordKey,
} from '@omen-quest/grid-map';

import type {
  GridCombatEngineState,
  GridCommittedAction, GridCombatInput,
  GridPlayerDefinition, GridAvailableAction,
} from '@omen-quest/combat-engine';
import {
  createGridCombatState, stepGridCombat, computeGridAvailableActions,
  GRID_COMBAT_SCENARIOS, getGridScenarioById, resolveEnemyPlacements,
  ADVENTURER_TEMPLATES, getTemplatesByKit,
  getGridTileMoveCost,
} from '@omen-quest/combat-engine';

// ============================================================================
// RENDER CONFIG
// ============================================================================

const COMBAT_RENDER_CONFIG: RenderConfig = {
  ...DEFAULT_RENDER_CONFIG,
  tileSize: 28,
  fogEnabled: true,
  showGrid: true,
  showMinimap: true,
  showTriggers: false,
  showDebugVis: false,
};

// ============================================================================
// TYPES
// ============================================================================

type UiMode = 'scenario_select' | 'party_select' | 'playing';
type ActionMode = 'none' | 'attack' | 'skill' | 'spell';

interface PlannedAction {
  action: GridCommittedAction;
  label: string;
}

// ============================================================================
// HELPERS
// ============================================================================

/** Convert adventurer templates to grid player definitions with spawn positions. */
function templateToGridPlayer(
  template: typeof ADVENTURER_TEMPLATES[0],
  startX: number,
  startY: number,
): GridPlayerDefinition {
  return {
    name: template.name,
    vitals: template.vitals,
    vitalsMax: template.vitalsMax,
    resolve: template.resolve,
    resolveMax: template.resolveMax,
    stats: { ...template.stats },
    tags: template.tags,
    equipment: template.equipment,
    magicDiscipline: template.magicDiscipline,
    skillIds: template.skillIds,
    defense: template.defense,
    startX,
    startY,
  };
}

/** Sync combat engine positions into the GridMapState entities. */
function syncPositions(
  mapState: GridMapState,
  combatState: GridCombatEngineState,
): GridMapState {
  let map = mapState.map;
  // Update entity positions from combat state
  const updatedEntities = map.entities.map(entity => {
    const combatPos = combatState.positions[entity.id];
    if (combatPos) {
      return { ...entity, x: combatPos.x, y: combatPos.y };
    }
    return entity;
  });
  map = { ...map, entities: updatedEntities };

  // Recompute fog from union of all living player positions
  const playerIds = combatState.participants
    .filter(p => p.type === 'player' && !p.isDefeated)
    .map(p => p.id);

  const fogGrid = mapState.fogGrid.map(row => [...row]);
  const lightGrid = mapState.lightGrid.map(row => [...row]);

  // Demote all visible to explored first
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      if (fogGrid[y][x] === 'visible') fogGrid[y][x] = 'explored';
    }
  }

  // Union FOV from all players
  for (const pid of playerIds) {
    const pos = combatState.positions[pid];
    if (!pos) continue;
    const vis = computeVisibility(map, pos.x, pos.y, 12, mapState.openDoors);
    for (const key of vis.visibleSet) {
      const [vx, vy] = key.split(',').map(Number);
      if (vx >= 0 && vx < map.width && vy >= 0 && vy < map.height) {
        fogGrid[vy][vx] = 'visible';
        lightGrid[vy][vx] = vis.lightValues.get(key) ?? 0;
      }
    }
  }

  return { ...mapState, map, fogGrid, lightGrid };
}

// ============================================================================
// COMPONENT
// ============================================================================

export function GridCombatSimulator() {
  // ---- Top-level state ----
  const [uiMode, setUiMode] = useState<UiMode>('scenario_select');
  const [selectedScenarioId, setSelectedScenarioId] = useState(GRID_COMBAT_SCENARIOS[0]?.id ?? '');
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([
    ADVENTURER_TEMPLATES[0]?.id ?? '',
    ADVENTURER_TEMPLATES[2]?.id ?? '',
  ]);

  // ---- Game state ----
  const [gridMap, setGridMap] = useState<GridMap | null>(null);
  const [mapState, setMapState] = useState<GridMapState | null>(null);
  const [combatState, setCombatState] = useState<GridCombatEngineState | null>(null);

  // ---- UI interaction state ----
  const [activeHeroId, setActiveHeroId] = useState<string | null>(null);
  const [actionMode, setActionMode] = useState<ActionMode>('none');
  const [plannedActions, setPlannedActions] = useState<Record<string, PlannedAction[]>>({});
  const logEndRef = useRef<HTMLDivElement>(null);

  // ---- WASD movement state ----
  const [startPositions, setStartPositions] = useState<Record<string, { x: number; y: number }>>({});
  const [movementBudget, setMovementBudget] = useState<Record<string, number>>({});
  const [stepsUsed, setStepsUsed] = useState<Record<string, number>>({});
  const [liveAvailableActions, setLiveAvailableActions] = useState<Record<string, GridAvailableAction[]>>({});

  // Scroll combat log on new entries
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [combatState?.combatLog.length]);

  // ============================================================================
  // MOVEMENT (shared by keyboard + touch D-pad)
  // ============================================================================

  /** Move active hero one tile in the given direction. Used by both WASD keys and touch D-pad. */
  const moveActiveHero = useCallback((dir: MoveDirection) => {
    if (!mapState || !combatState || !activeHeroId) return;
    if (combatState.phase !== 'player_commit') return;

    // Check movement budget
    const budget = movementBudget[activeHeroId] ?? 0;
    if (budget <= 0) return;

    // Ensure mapState.playerId is set to the active hero
    const stateWithPlayer = mapState.playerId === activeHeroId
      ? mapState
      : { ...mapState, playerId: activeHeroId };

    const newMapState = tryMove(stateWithPlayer, dir);
    if (newMapState === stateWithPlayer) {
      // Blocked — still persist the playerId change if needed
      if (stateWithPlayer !== mapState) setMapState(stateWithPlayer);
      return;
    }

    // Deduct terrain-aware movement cost (water = 2, normal = 1)
    const movedEntity = newMapState.map.entities.find(ent => ent.id === activeHeroId);
    const tileCost = movedEntity && gridMap ? getGridTileMoveCost(gridMap, movedEntity.x, movedEntity.y) : 1;

    // Block move if budget can't cover the terrain cost
    if (budget < tileCost) {
      if (stateWithPlayer !== mapState) setMapState(stateWithPlayer);
      return;
    }

    setMapState(newMapState);
    setMovementBudget(prev => ({ ...prev, [activeHeroId]: (prev[activeHeroId] ?? 0) - tileCost }));
    setStepsUsed(prev => ({ ...prev, [activeHeroId]: (prev[activeHeroId] ?? 0) + 1 }));

    // Recompute available actions from the hero's new position
    const heroEntity = movedEntity;
    if (heroEntity && gridMap) {
      const tempPositions = { ...combatState.positions, [activeHeroId]: { x: heroEntity.x, y: heroEntity.y } };
      const tempState = { ...combatState, positions: tempPositions };
      const actions = computeGridAvailableActions(activeHeroId, tempState, gridMap);
      setLiveAvailableActions(prev => ({ ...prev, [activeHeroId]: actions }));
    }
  }, [mapState, combatState, activeHeroId, movementBudget, gridMap]);

  // Keyboard handler — delegates to moveActiveHero
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (!mapState || !combatState || !activeHeroId) return;
      if (combatState.phase !== 'player_commit') return;
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLSelectElement ||
        e.target instanceof HTMLTextAreaElement
      ) return;

      const dirMap: Record<string, MoveDirection> = {
        w: 'up', W: 'up', ArrowUp: 'up',
        s: 'down', S: 'down', ArrowDown: 'down',
        a: 'left', A: 'left', ArrowLeft: 'left',
        d: 'right', D: 'right', ArrowRight: 'right',
      };

      const dir = dirMap[e.key];
      if (!dir) return;
      e.preventDefault();
      moveActiveHero(dir);
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mapState, combatState, activeHeroId, moveActiveHero]);

  /** Switch active hero — updates playerId, recomputes FOV from their position. */
  const switchHero = useCallback((heroId: string) => {
    setActiveHeroId(heroId);
    setActionMode('none');
    if (mapState) {
      const entity = mapState.map.entities.find(e => e.id === heroId);
      if (entity) {
        const vis = computeVisibility(mapState.map, entity.x, entity.y, 12, mapState.openDoors);
        const fogGrid = mapState.fogGrid.map(row => [...row]);
        const lightGrid = mapState.lightGrid.map(row => [...row]);
        // Demote visible → explored, then apply new hero's FOV
        for (let y = 0; y < mapState.map.height; y++) {
          for (let x = 0; x < mapState.map.width; x++) {
            if (fogGrid[y][x] === 'visible') fogGrid[y][x] = 'explored';
          }
        }
        for (const key of vis.visibleSet) {
          const [vx, vy] = key.split(',').map(Number);
          if (vx >= 0 && vx < mapState.map.width && vy >= 0 && vy < mapState.map.height) {
            fogGrid[vy][vx] = 'visible';
            lightGrid[vy][vx] = vis.lightValues.get(key) ?? 0;
          }
        }
        setMapState({ ...mapState, playerId: heroId, fogGrid, lightGrid });
      }
    }
  }, [mapState]);

  /** Reset a hero's position to their round-start position and restore budget. */
  const resetHeroPosition = useCallback((heroId: string) => {
    if (!mapState || !combatState) return;
    const start = startPositions[heroId];
    if (!start) return;

    // Find the hero entity and move it back
    const entities = mapState.map.entities.map(e =>
      e.id === heroId ? { ...e, x: start.x, y: start.y } : e,
    );
    const updatedMap = { ...mapState.map, entities };

    // Recompute FOV from start position
    const vis = computeVisibility(updatedMap, start.x, start.y, 12, mapState.openDoors);
    const fogGrid = mapState.fogGrid.map(row => [...row]);
    const lightGrid = mapState.lightGrid.map(row => [...row]);
    for (let y = 0; y < updatedMap.height; y++) {
      for (let x = 0; x < updatedMap.width; x++) {
        if (fogGrid[y][x] === 'visible') fogGrid[y][x] = 'explored';
      }
    }
    for (const key of vis.visibleSet) {
      const [vx, vy] = key.split(',').map(Number);
      if (vx >= 0 && vx < updatedMap.width && vy >= 0 && vy < updatedMap.height) {
        fogGrid[vy][vx] = 'visible';
        lightGrid[vy][vx] = vis.lightValues.get(key) ?? 0;
      }
    }
    setMapState({ ...mapState, map: updatedMap, fogGrid, lightGrid });

    // Restore movement budget (AP × speed = total tiles)
    const participant = combatState.participants.find(p => p.id === heroId);
    if (participant) {
      setMovementBudget(prev => ({ ...prev, [heroId]: participant.apRemaining * participant.speed }));
      setStepsUsed(prev => ({ ...prev, [heroId]: 0 }));
    }

    // Clear live available actions (will use engine's pre-computed from start pos)
    setLiveAvailableActions(prev => {
      const next = { ...prev };
      delete next[heroId];
      return next;
    });

    // Clear planned combat actions too (position changed)
    setPlannedActions(prev => {
      const next = { ...prev };
      delete next[heroId];
      return next;
    });
  }, [mapState, combatState, startPositions]);

  // ============================================================================
  // SCENARIO & PARTY SELECTION
  // ============================================================================

  const scenario = useMemo(() =>
    getGridScenarioById(selectedScenarioId),
    [selectedScenarioId]
  );

  const selectedTemplates = useMemo(() =>
    selectedTemplateIds
      .map(id => ADVENTURER_TEMPLATES.find(t => t.id === id))
      .filter(Boolean) as typeof ADVENTURER_TEMPLATES,
    [selectedTemplateIds]
  );

  const templatesByKit = useMemo(() => getTemplatesByKit(), []);

  function toggleTemplate(id: string) {
    setSelectedTemplateIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 4) return prev; // Max 4 heroes
      return [...prev, id];
    });
  }

  // ============================================================================
  // START COMBAT
  // ============================================================================

  function startCombat() {
    if (!scenario || selectedTemplates.length === 0) return;

    // Generate the map
    const map = generateMap(scenario.mapConfig);

    // Resolve player spawn
    let spawnX: number, spawnY: number;
    if (scenario.playerSpawn === 'entrance') {
      const entrance = map.metadata?.rooms?.find(r => r.purpose === 'entrance');
      if (entrance) {
        spawnX = Math.floor(entrance.x + entrance.width / 2);
        spawnY = Math.floor(entrance.y + entrance.height / 2);
      } else {
        // Fallback: find first floor tile
        spawnX = Math.floor(map.width / 4);
        spawnY = Math.floor(map.height / 2);
      }
    } else {
      spawnX = scenario.playerSpawn.x;
      spawnY = scenario.playerSpawn.y;
    }

    // Create player definitions with staggered positions
    const players: GridPlayerDefinition[] = selectedTemplates.map((t, i) =>
      templateToGridPlayer(t, spawnX + (i % 2), spawnY + Math.floor(i / 2))
    );

    // Resolve enemy placements
    const rooms = map.metadata?.rooms ?? [];
    const entranceRoom = rooms.find(r => r.purpose === 'entrance');
    const enemies = resolveEnemyPlacements(scenario, rooms, entranceRoom);

    // Add entities to the map
    let mapWithEntities = map;
    for (let i = 0; i < players.length; i++) {
      const entity: Entity = {
        id: `player_${i}`,
        x: players[i].startX,
        y: players[i].startY,
        type: 'player',
        name: players[i].name,
        lightRadius: 12,
      };
      mapWithEntities = {
        ...mapWithEntities,
        entities: [...mapWithEntities.entities, entity],
      };
    }
    for (let i = 0; i < enemies.length; i++) {
      const entity: Entity = {
        id: `npc_${i}`,
        x: enemies[i].startX,
        y: enemies[i].startY,
        type: 'monster',
        name: enemies[i].name,
      };
      mapWithEntities = {
        ...mapWithEntities,
        entities: [...mapWithEntities.entities, entity],
      };
    }

    // Create grid map state (for rendering)
    const initialMapState = createGridMapState(mapWithEntities, 'player_0');

    // Create combat state
    const combatConfig = { players, enemies };
    const initialCombatState = createGridCombatState(combatConfig, mapWithEntities);

    // Sync and set
    const synced = syncPositions(initialMapState, initialCombatState);
    setGridMap(mapWithEntities);
    setMapState({ ...synced, playerId: 'player_0' });
    setCombatState(initialCombatState);
    setActiveHeroId('player_0');
    setActionMode('none');
    setPlannedActions({});

    // Initialize WASD movement state (budget = AP × speed = total tiles available)
    const positions: Record<string, { x: number; y: number }> = {};
    const budgets: Record<string, number> = {};
    for (const p of initialCombatState.participants) {
      if (p.type === 'player' && !p.isDefeated) {
        const pos = initialCombatState.positions[p.id];
        positions[p.id] = { x: pos.x, y: pos.y };
        budgets[p.id] = p.apRemaining * p.speed;
      }
    }
    setStartPositions(positions);
    setMovementBudget(budgets);
    setStepsUsed({});
    setLiveAvailableActions({});

    setUiMode('playing');
  }

  // ============================================================================
  // COMBAT ACTIONS
  // ============================================================================

  const activeHero = useMemo(() =>
    combatState?.participants.find(p => p.id === activeHeroId),
    [combatState, activeHeroId]
  );

  const availableActions = useMemo(() => {
    if (!activeHeroId || !combatState) return [];
    // Use live-recomputed actions if the hero has moved via WASD
    if (liveAvailableActions[activeHeroId]) return liveAvailableActions[activeHeroId];
    return combatState.availableActions[activeHeroId] ?? [];
  }, [combatState, activeHeroId, liveAvailableActions]);

  const heroPlannedActions = useMemo(() =>
    activeHeroId ? (plannedActions[activeHeroId] ?? []) : [],
    [plannedActions, activeHeroId]
  );

  const plannedAPCost = useMemo(() =>
    heroPlannedActions.reduce((sum, a) => sum + a.action.apSpent, 0),
    [heroPlannedActions]
  );

  /** Estimated movement AP cost for the active hero (optimal path from start→current). */
  const movementAPCost = useMemo(() => {
    if (!activeHeroId || !mapState || !gridMap || !combatState) return 0;
    const start = startPositions[activeHeroId];
    if (!start) return 0;
    const entity = mapState.map.entities.find(e => e.id === activeHeroId);
    if (!entity || (entity.x === start.x && entity.y === start.y)) return 0;
    const participant = combatState.participants.find(p => p.id === activeHeroId);
    if (!participant) return 0;
    const path = findPath(gridMap, start, { x: entity.x, y: entity.y }, mapState.openDoors, undefined, getGridTileMoveCost);
    if (path.length === 0) return 0;
    // Sum terrain costs along path (exclude start tile)
    let pathMP = 0;
    for (let i = 1; i < path.length; i++) {
      pathMP += getGridTileMoveCost(gridMap, path[i].x, path[i].y);
    }
    return Math.ceil(pathMP / participant.speed);
  }, [activeHeroId, mapState, gridMap, startPositions, combatState]);

  /** Add an action to the active hero's planned actions. */
  function addPlannedAction(action: GridCommittedAction, label: string) {
    if (!activeHeroId) return;
    setPlannedActions(prev => ({
      ...prev,
      [activeHeroId]: [...(prev[activeHeroId] ?? []), { action, label }],
    }));
    setActionMode('none');
  }

  /** Clear a hero's planned actions. */
  function clearPlannedActions(heroId: string) {
    setPlannedActions(prev => {
      const next = { ...prev };
      delete next[heroId];
      return next;
    });
  }

  /** Handle cell click based on current action mode. */
  const handleCellClick = useCallback((x: number, y: number) => {
    if (!combatState || !activeHeroId) return;

    if (actionMode === 'attack') {
      const attackAction = availableActions.find(a => a.type === 'attack');
      if (!attackAction?.targets) return;
      const target = attackAction.targets.find(t => t.position.x === x && t.position.y === y);
      if (target) {
        addPlannedAction(
          { actionId: 'attack', actionType: 'attack', targetId: target.id, apSpent: attackAction.apCost },
          `Attack ${target.name}`,
        );
      }
    } else if (actionMode === 'spell') {
      const spellAction = availableActions.find(a => a.type === 'magic');
      if (!spellAction?.targets) return;
      const target = spellAction.targets.find(t => t.position.x === x && t.position.y === y);
      if (target) {
        addPlannedAction(
          { actionId: spellAction.id, actionType: 'magic', targetId: target.id, apSpent: spellAction.apCost, resolveSpent: spellAction.resolveCost },
          `${spellAction.name} → ${target.name}`,
        );
      }
    }
  }, [combatState, activeHeroId, actionMode, availableActions]);

  // ============================================================================
  // PHASE EXECUTION
  // ============================================================================

  /** Commit all player actions and advance to player_resolve. */
  function commitAllPlayers() {
    if (!combatState || !gridMap || !mapState || combatState.phase !== 'player_commit') return;

    let state = combatState;

    // Build occupied set (all living participants) for pathfinding
    const occupied = new Set<string>();
    for (const p of state.participants) {
      if (!p.isDefeated) {
        const pos = state.positions[p.id];
        if (pos) occupied.add(coordKey(pos.x, pos.y));
      }
    }

    // Submit each player's planned actions, prepending move if hero moved
    const livingPlayers = state.participants.filter(p => p.type === 'player' && !p.isDefeated);
    for (const player of livingPlayers) {
      const combatActions = plannedActions[player.id]?.map(pa => pa.action) ?? [];
      const actions: GridCommittedAction[] = [];

      // Check if hero moved from start position
      const start = startPositions[player.id];
      const entity = mapState.map.entities.find(e => e.id === player.id);
      if (start && entity && (entity.x !== start.x || entity.y !== start.y)) {
        // Compute optimal path cost with terrain awareness (exclude self from occupied)
        const selfOccupied = new Set(occupied);
        selfOccupied.delete(coordKey(start.x, start.y));
        const path = findPath(
          gridMap, { x: start.x, y: start.y }, { x: entity.x, y: entity.y },
          mapState.openDoors, selfOccupied, getGridTileMoveCost,
        );
        if (path.length > 1) {
          // Sum terrain costs along path (exclude start tile)
          let pathMP = 0;
          for (let i = 1; i < path.length; i++) {
            pathMP += getGridTileMoveCost(gridMap, path[i].x, path[i].y);
          }
          const moveCost = Math.ceil(pathMP / player.speed);
          if (moveCost > 0) {
            actions.push({
              actionId: `move_${entity.x}_${entity.y}`,
              actionType: 'move',
              apSpent: moveCost,
            });
          }
        }
      }

      actions.push(...combatActions);

      const input: GridCombatInput = {
        type: 'player_commit',
        participantId: player.id,
        actions,
      };
      state = stepGridCombat(state, gridMap, input);
    }

    setCombatState(state);
    setPlannedActions({});
    setLiveAvailableActions({});

    // Auto-advance through player_resolve
    if (state.phase === 'player_resolve') {
      setTimeout(() => advancePhase(), 300);
    }
  }

  /** Advance the current phase (auto-phases: player_resolve, enemy_turn, round_end). */
  function advancePhase() {
    if (!combatState || !gridMap) return;

    let state = stepGridCombat(combatState, gridMap, { type: 'advance' });

    // Sync positions after resolution
    if (mapState) {
      setMapState(syncPositions(mapState, state));
    }

    setCombatState(state);

    // Auto-advance chain: player_resolve → enemy_turn → (reaction or round_end)
    if (state.phase === 'enemy_turn') {
      setTimeout(() => {
        const nextState = stepGridCombat(state, gridMap!, { type: 'advance' });
        if (mapState) setMapState(syncPositions(mapState, nextState));
        setCombatState(nextState);

        // If no threats, skip to round_end
        if (nextState.phase === 'round_end') {
          setTimeout(() => advanceRoundEnd(nextState), 300);
        }
      }, 500);
    } else if (state.phase === 'round_end') {
      setTimeout(() => advanceRoundEnd(state), 300);
    }
  }

  function advanceRoundEnd(state?: GridCombatEngineState) {
    const s = state ?? combatState;
    if (!s || !gridMap || s.phase !== 'round_end') return;

    const nextState = stepGridCombat(s, gridMap, { type: 'advance' });
    const firstAliveHero = nextState.participants.find(p => p.type === 'player' && !p.isDefeated);
    if (mapState) {
      const synced = syncPositions(mapState, nextState);
      setMapState(firstAliveHero ? { ...synced, playerId: firstAliveHero.id } : synced);
    }
    setCombatState(nextState);
    setActiveHeroId(firstAliveHero?.id ?? null);
    setActionMode('none');

    // Reset WASD movement state for new round (budget = AP × speed)
    const positions: Record<string, { x: number; y: number }> = {};
    const budgets: Record<string, number> = {};
    for (const p of nextState.participants) {
      if (p.type === 'player' && !p.isDefeated) {
        const pos = nextState.positions[p.id];
        positions[p.id] = { x: pos.x, y: pos.y };
        budgets[p.id] = p.apRemaining * p.speed;
      }
    }
    setStartPositions(positions);
    setMovementBudget(budgets);
    setStepsUsed({});
    setLiveAvailableActions({});
  }

  /** Submit reactions for the current player (simplified: auto-block all). */
  function submitReactions() {
    if (!combatState || !gridMap || combatState.phase !== 'reaction') return;

    let state = combatState;
    const playersWithThreats = Object.keys(state.incomingThreats);

    for (const pid of playersWithThreats) {
      const threats = state.incomingThreats[pid] ?? [];
      const player = state.participants.find(p => p.id === pid);
      if (!player || player.isDefeated) continue;

      // Auto-block each threat if AP allows
      const reactions = threats
        .filter(() => player.apRemaining >= 1)
        .map(threat => {
          player.apRemaining -= 1; // Approximate deduction for planning
          return {
            threatId: threat.id,
            reactionType: 'block' as const,
            apSpent: 1,
          };
        });

      const input: GridCombatInput = {
        type: 'reactions',
        participantId: pid,
        reactions,
      };
      state = stepGridCombat(state, gridMap, input);
    }

    if (mapState) setMapState(syncPositions(mapState, state));
    setCombatState(state);

    if (state.phase === 'round_end') {
      setTimeout(() => advanceRoundEnd(state), 300);
    }
  }

  /** Reset combat. */
  function resetCombat() {
    setGridMap(null);
    setMapState(null);
    setCombatState(null);
    setPlannedActions({});
    setActionMode('none');
    setActiveHeroId(null);
    setStartPositions({});
    setMovementBudget({});
    setStepsUsed({});
    setLiveAvailableActions({});
    setUiMode('scenario_select');
  }

  // ============================================================================
  // OVERLAY COMPUTATION
  // ============================================================================

  const overlays = useMemo(() => {
    const highlighted: TileHighlight[] = [];
    const path: PathPreviewPoint[] = [];
    const indicators: TargetIndicator[] = [];

    if (!combatState || !activeHeroId || combatState.phase !== 'player_commit') {
      return { highlighted, path, indicators };
    }

    if (actionMode === 'attack') {
      const attackAction = availableActions.find(a => a.type === 'attack');
      if (attackAction?.targets) {
        for (const t of attackAction.targets) {
          indicators.push({ x: t.position.x, y: t.position.y, type: 'attack' });
        }
      }
    } else if (actionMode === 'spell') {
      const spellAction = availableActions.find(a => a.type === 'magic');
      if (spellAction?.targets) {
        for (const t of spellAction.targets) {
          indicators.push({ x: t.position.x, y: t.position.y, type: 'spell' });
        }
      }
    }

    return { highlighted, path, indicators };
  }, [combatState, activeHeroId, actionMode, availableActions]);

  // ============================================================================
  // RENDER: SCENARIO SELECT
  // ============================================================================

  if (uiMode === 'scenario_select') {
    return (
      <div className="min-h-screen bg-black text-white p-4">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-neutral-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
            <h1 className="text-xl font-bold flex items-center gap-2"><Swords className="w-5 h-5" /> Grid Combat Simulator</h1>
          </div>

          <div className="space-y-3">
            <h2 className="text-sm font-medium text-neutral-400 uppercase tracking-wider">Select Scenario</h2>
            <div className="grid gap-2">
              {GRID_COMBAT_SCENARIOS.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSelectedScenarioId(s.id)}
                  className={cn(
                    'p-3 rounded border text-left transition-colors',
                    s.id === selectedScenarioId
                      ? 'border-amber-500/50 bg-amber-500/10'
                      : 'border-white/10 bg-white/5 hover:border-white/20'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{s.name}</span>
                    <span className="text-xs text-neutral-500 uppercase">{s.category}</span>
                  </div>
                  <p className="text-xs text-neutral-400 mt-1">{s.description}</p>
                  <p className="text-xs text-neutral-500 mt-1">
                    {s.enemies.length} enemies | {s.mapConfig.type} map ({s.mapConfig.width}x{s.mapConfig.height})
                  </p>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => setUiMode('party_select')}
            disabled={!scenario}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 rounded font-medium text-sm"
          >
            Select Party →
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: PARTY SELECT
  // ============================================================================

  if (uiMode === 'party_select') {
    return (
      <div className="min-h-screen bg-black text-white p-4">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="flex items-center gap-3">
            <button onClick={() => setUiMode('scenario_select')} className="text-neutral-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold">Select Party (2-4 heroes)</h1>
          </div>

          {Object.entries(templatesByKit).map(([kit, templates]) => (
            <div key={kit} className="space-y-2">
              <h3 className="text-sm font-medium text-neutral-400 uppercase tracking-wider">{kit}</h3>
              <div className="grid gap-2">
                {templates.map(t => (
                  <button
                    key={t.id}
                    onClick={() => toggleTemplate(t.id)}
                    className={cn(
                      'p-2 rounded border text-left text-sm transition-colors',
                      selectedTemplateIds.includes(t.id)
                        ? 'border-amber-500/50 bg-amber-500/10'
                        : 'border-white/10 bg-white/5 hover:border-white/20'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{t.name}</span>
                      <span className="text-xs text-neutral-500">{t.ancestry}</span>
                    </div>
                    <p className="text-xs text-neutral-400">{t.description}</p>
                    <div className="flex gap-2 mt-1 text-xs text-neutral-500">
                      <span>HP:{t.vitals}</span>
                      <span>Res:{t.resolve}</span>
                      <span>B:{t.stats.brawn} G:{t.stats.grace} W:{t.stats.wits} S:{t.stats.spirit}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div className="flex gap-3">
            <button
              onClick={startCombat}
              disabled={selectedTemplates.length < 2}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 rounded font-medium text-sm flex items-center gap-2"
            >
              <Swords className="w-4 h-4" /> Start Combat ({selectedTemplates.length} heroes)
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: PLAYING
  // ============================================================================

  if (!combatState || !mapState || !gridMap) return null;

  // const livingPlayers = combatState.participants.filter(p => p.type === 'player' && !p.isDefeated);
  // const allPlayersCommitted = livingPlayers.every(p => p.hasCommitted);
  const phaseLabel = {
    player_commit: 'Player Planning',
    player_resolve: 'Resolving Actions...',
    enemy_turn: 'Enemy Turn...',
    reaction: 'React to Threats',
    round_end: 'Round Ending...',
    ended: combatState.outcome === 'victory' ? 'Victory!' : 'Defeat',
  }[combatState.phase];

  return (
    <div className="h-screen bg-black text-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-white/10 bg-white/5 flex-shrink-0">
        <button onClick={resetCombat} className="text-neutral-400 hover:text-white"><ArrowLeft className="w-4 h-4" /></button>
        <Swords className="w-4 h-4 text-amber-400" />
        <span className="font-medium text-sm">{scenario?.name}</span>
        <span className="text-xs text-neutral-500">Round {combatState.roundNumber}</span>
        <span className={cn(
          'text-xs px-2 py-0.5 rounded',
          combatState.phase === 'player_commit' ? 'bg-blue-500/20 text-blue-400' :
          combatState.phase === 'reaction' ? 'bg-amber-500/20 text-amber-400' :
          combatState.phase === 'ended' ? (combatState.outcome === 'victory' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400') :
          'bg-white/10 text-neutral-400'
        )}>
          {phaseLabel}
        </span>
        <div className="flex-1" />
        <button onClick={resetCombat} className="text-xs text-neutral-500 hover:text-white flex items-center gap-1">
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
      </div>

      {/* Main layout: sidebar + canvas + log */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left sidebar: participants */}
        <div className="w-80 border-r border-white/10 flex flex-col overflow-hidden flex-shrink-0">
          <div className="px-2 py-1.5 bg-white/5 border-b border-white/10 text-xs font-medium text-neutral-400 flex items-center gap-1">
            <Users className="w-3 h-3" /> Participants
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {/* Players */}
            <div className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1">Heroes</div>
            {combatState.participants.filter(p => p.type === 'player').map(p => (
              <ParticipantRow
                key={p.id}
                participant={p}
                isSelected={p.id === activeHeroId}
                onClick={() => combatState.phase === 'player_commit' && switchHero(p.id)}
                compact
              />
            ))}

            {/* Enemies */}
            <div className="text-[10px] text-neutral-500 uppercase tracking-wider mt-3 mb-1">Enemies</div>
            {combatState.participants.filter(p => p.type === 'npc').map(p => (
              <ParticipantRow key={p.id} participant={p} compact />
            ))}

            {/* Planned actions for active hero */}
            {combatState.phase === 'player_commit' && activeHero && (
              <div className="mt-3 border-t border-white/10 pt-2">
                <div className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1">
                  {activeHero.name}'s Plan ({movementAPCost + plannedAPCost}/{activeHero.apRemaining} AP)
                </div>
                {movementAPCost > 0 && (
                  <div className="text-xs text-blue-400 py-0.5 px-1 bg-blue-500/10 rounded mb-0.5">
                    <Footprints className="w-3 h-3 inline mr-1" />
                    Move <span className="text-neutral-500">({movementAPCost} AP)</span>
                  </div>
                )}
                {heroPlannedActions.length > 0 ? (
                  <div className="space-y-0.5">
                    {heroPlannedActions.map((pa, i) => (
                      <div key={i} className="text-xs text-neutral-300 py-0.5 px-1 bg-white/5 rounded">
                        {pa.label} <span className="text-neutral-500">({pa.action.apSpent} AP)</span>
                      </div>
                    ))}
                    <button
                      onClick={() => clearPlannedActions(activeHero.id)}
                      className="text-xs text-red-400 hover:text-red-300 mt-1"
                    >
                      Clear
                    </button>
                  </div>
                ) : movementAPCost === 0 ? (
                  <p className="text-xs text-neutral-500 italic">WASD to move, then pick actions</p>
                ) : null}
              </div>
            )}

            {/* Incoming threats (reaction phase) */}
            {combatState.phase === 'reaction' && (
              <div className="mt-3 border-t border-white/10 pt-2">
                <div className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1">Incoming Threats</div>
                {Object.entries(combatState.incomingThreats).map(([pid, threats]) => (
                  <div key={pid} className="space-y-0.5">
                    {threats.map(threat => (
                      <div key={threat.id} className="text-xs text-red-400 py-0.5 px-1 bg-red-500/10 rounded">
                        {threat.attackerName}: {threat.actionName} ({threat.estimatedDamage})
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Center: Canvas */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <GridMapCanvas
            state={mapState}
            config={COMBAT_RENDER_CONFIG}
            onCellClick={handleCellClick}
            onMoveRequest={combatState.phase === 'player_commit' ? moveActiveHero : undefined}
            className="flex-1"
            highlightedTiles={overlays.highlighted}
            pathPreview={overlays.path}
            targetIndicators={overlays.indicators}
            activeEntityId={activeHeroId ?? undefined}
          />

          {/* Action bar */}
          <div className="border-t border-white/10 bg-white/5 px-3 py-2 flex items-center gap-2 flex-shrink-0">
            {combatState.phase === 'player_commit' && (
              <>
                {/* Movement budget display */}
                {activeHeroId && (
                  <div className="flex items-center gap-1.5 text-xs mr-1">
                    <Footprints className="w-3.5 h-3.5 text-blue-400" />
                    <span className={cn(
                      'font-medium',
                      (movementBudget[activeHeroId] ?? 0) > 0 ? 'text-blue-400' : 'text-neutral-500',
                    )}>
                      {movementBudget[activeHeroId] ?? 0} tiles
                    </span>
                    {(stepsUsed[activeHeroId] ?? 0) > 0 && (
                      <button
                        onClick={() => resetHeroPosition(activeHeroId)}
                        className="text-neutral-500 hover:text-white ml-0.5"
                        title="Reset position"
                      >
                        <RotateCcw className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )}

                <div className="w-px h-5 bg-white/10" />

                <ActionButton
                  icon={<Crosshair className="w-4 h-4" />}
                  label="Attack"
                  active={actionMode === 'attack'}
                  disabled={!availableActions.some(a => a.type === 'attack')}
                  onClick={() => setActionMode(actionMode === 'attack' ? 'none' : 'attack')}
                />
                {availableActions.some(a => a.type === 'skill') && (
                  <ActionButton
                    icon={<Sparkles className="w-4 h-4" />}
                    label="Skill"
                    active={actionMode === 'skill'}
                    onClick={() => setActionMode(actionMode === 'skill' ? 'none' : 'skill')}
                  />
                )}
                {availableActions.some(a => a.type === 'magic') && (
                  <ActionButton
                    icon={<Sparkles className="w-4 h-4" />}
                    label="Spell"
                    active={actionMode === 'spell'}
                    onClick={() => setActionMode(actionMode === 'spell' ? 'none' : 'spell')}
                  />
                )}
                <ActionButton
                  icon={<ShieldIcon className="w-4 h-4" />}
                  label="Defend"
                  disabled={!availableActions.some(a => a.type === 'defend')}
                  onClick={() => {
                    const defend = availableActions.find(a => a.type === 'defend');
                    if (defend) {
                      addPlannedAction(
                        { actionId: 'defend', actionType: 'defend', apSpent: defend.apCost },
                        'Defend',
                      );
                    }
                  }}
                />

                <div className="flex-1" />

                <button
                  onClick={commitAllPlayers}
                  className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 rounded text-sm font-medium flex items-center gap-1"
                >
                  <Play className="w-4 h-4" /> Execute Round
                </button>
              </>
            )}

            {combatState.phase === 'reaction' && (
              <>
                <span className="text-xs text-neutral-400">Threats incoming! React or skip.</span>
                <div className="flex-1" />
                <button
                  onClick={submitReactions}
                  className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 rounded text-sm font-medium flex items-center gap-1"
                >
                  <ShieldIcon className="w-4 h-4" /> Auto-Block & Continue
                </button>
              </>
            )}

            {combatState.phase === 'ended' && (
              <>
                <span className={cn(
                  'text-sm font-bold',
                  combatState.outcome === 'victory' ? 'text-green-400' : 'text-red-400'
                )}>
                  {combatState.outcome === 'victory' ? 'Victory!' : 'Defeat'}
                </span>
                <div className="flex-1" />
                <button
                  onClick={resetCombat}
                  className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded text-sm font-medium flex items-center gap-1"
                >
                  <RotateCcw className="w-4 h-4" /> New Scenario
                </button>
              </>
            )}

            {(combatState.phase === 'player_resolve' || combatState.phase === 'enemy_turn' || combatState.phase === 'round_end') && (
              <span className="text-xs text-neutral-400 animate-pulse">Processing...</span>
            )}
          </div>
        </div>

        {/* Right sidebar: combat log */}
        <div className="w-72 border-l border-white/10 flex flex-col overflow-hidden flex-shrink-0">
          <CombatLogViewer log={combatState.combatLog} maxHeight="max-h-[calc(100vh-8rem)]" />
          <div ref={logEndRef} />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ACTION BUTTON
// ============================================================================

function ActionButton({
  icon, label, active, disabled, onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'px-2 py-1.5 rounded text-xs font-medium flex items-center gap-1 transition-colors',
        active ? 'bg-amber-600 text-white' :
        disabled ? 'bg-white/5 text-neutral-600 cursor-not-allowed' :
        'bg-white/10 text-neutral-300 hover:bg-white/20'
      )}
    >
      {icon} {label}
    </button>
  );
}
